/* eslint-disable arrow-body-style */ // Disagree with this.
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";

import apiCalls from "../../../PowerShadesAPIFunctions";
import { extraEntityReducersFunction, loadStatus } from "../entitiesType";

import type { AppState } from "../../index";
import type { QuoteAccessoryStore } from "../../../powershadesApiTypes";
import type {
	AddAwningThunkInput,
	DeleteAwningThunkInput,
	UpdateAwningThunkInput
} from "./awningThunkTypes";
import { captureSentryError, extractErrorMessage, isFailedApiCall } from "../../../psUtil";
import { wentToQuote } from "../quotes";


const initialQuoteAwning: () => QuoteAccessoryStore = () => {
	const quoteAwning: QuoteAccessoryStore = {
		Id: 0,
		quote_id: 0,
		accessory_id: 0,
		quantity: 0,
		msrp: 0,
		name: "",
		room_name: "",
		shade_name: "",
		mfg_status: "",
		last_time_priced: -1,
		part_number: "",
		cost: 0,
		shipping: 0,
		dealer_sale_price: 0,
		base_sale_price: 0,
		line_number: 0,
		parameters: [],
		error_message: '',
		is_pricing: false,
		unlocked: false,
		selected: false,
	};

	return quoteAwning;
};

const loadQuoteAwnings = createAsyncThunk<
	QuoteAccessoryStore[],
	number,
	{
		rejectValue: string;
	}
>("entities/getQuoteAwnings", async (quoteId, { rejectWithValue, dispatch }) => {
	let quoteAwnings: QuoteAccessoryStore[] = [];
	try {
		await dispatch(wentToQuote(quoteId));
		const resp = await apiCalls.getQuoteAwnings(quoteId);

		if (isFailedApiCall(resp)) {
			throw resp.error;
		}

		quoteAwnings = resp.data.quote_accessories ?? [];
	} catch (err: any) {
		captureSentryError(err, {
			tags: {
				section: "redux"
			},
			extra: {
				redux_action: "getQuoteAwnings",
				file_origin: "src/Store/entities/Awnings/index.ts"
			}
		});

		const errorMessage = extractErrorMessage(err);
		return rejectWithValue(errorMessage);
	}

	return quoteAwnings;
});

// Thunk to add a quote Awning. The fancy promise is to true up the legacy quote object.
const addQuoteAwning = createAsyncThunk<
	QuoteAccessoryStore,
	AddAwningThunkInput,
	{
		rejectValue: string;
	}
>("entities/addQuoteAwning", async (input, { rejectWithValue, dispatch }) => {
	const { quoteId, sku, parms, nextLineNumber, room_name, shade_name } = input;

	let respData: QuoteAccessoryStore;
	await dispatch(wentToQuote(quoteId));

	try {
		const resp = await apiCalls.addQuoteAccessory({
			quoteId,
			sku,
			newQuantity: 1,
			parms,
			lineNumber: nextLineNumber,
			...(room_name !== undefined ? { room_name } : {}),
			...(shade_name !== undefined ? { shade_name } : {}),
		});

		if (isFailedApiCall(resp)) {
			throw resp.error;
		}

		const qa = resp.data.quote_accessories;
		if (!qa) {
			throw new Error("Expected quote accessory not found in response");
		}
		// No pricing comes back from this call. The awning is queued for pricing on the backend and
		// the prices arrive later over SignalR (`AssemblyUpdated`), which triggers a refetch.
		respData = qa;
	} catch (err: any) {
		captureSentryError(err, {
			tags: {
				section: "redux"
			},
			extra: {
				redux_action: "addQuoteAwning",
				file_origin: "src/Store/entities/Awnings/index.ts"
			}
		});

		const errorMessage = extractErrorMessage(err);
		return rejectWithValue(errorMessage);
	}
	return respData;
});

// Thunk to add a quote Awning. The fancy promise is to true up the legacy quote object.
const deleteQuoteAwning = createAsyncThunk<
	boolean,
	DeleteAwningThunkInput,
	{
		rejectValue: string;
	}
>("entities/deleteQuoteAwning", async (input, { rejectWithValue, dispatch }) => {
	const { quoteId, lineNumber, priceCallback } = input;

	try {
		await dispatch(wentToQuote(quoteId));
		const resp = await apiCalls.deleteQuoteAccessory(quoteId, lineNumber);

		if (isFailedApiCall(resp)) {
			throw resp.error;
		}

		priceCallback && priceCallback(resp.data.legacy_pricing);
	} catch (err: any) {
		captureSentryError(err, {
			tags: {
				section: "redux"
			},
			extra: {
				redux_action: "deleteQuoteAwning",
				file_origin: "src/Store/entities/Awnings/index.ts"
			}
		});

		const errorMessage = extractErrorMessage(err);
		return rejectWithValue(errorMessage);
	}

	return true;
});

// Thunk to add a quote Awning. The callback is for legacy quote object.
const updateQuoteAwning = createAsyncThunk<
	QuoteAccessoryStore,
	UpdateAwningThunkInput,
	{
		rejectValue: string;
		state: AppState;
	}
>("entities/updateQuoteAwning", async (input, { rejectWithValue, dispatch }) => {
	const { quoteId, lineNumber, parms, room_name, shade_name } = input;

	let quoteAwning: QuoteAccessoryStore;

	try {
		await dispatch(wentToQuote(quoteId));
		const resp = await apiCalls.updateQuoteAccessory({
			quoteId,
			lineNumber,
			newQuantity: 1,
			parms,
			...(room_name !== undefined ? { room_name } : {}),
			...(shade_name !== undefined ? { shade_name } : {}),
		});

		if (isFailedApiCall(resp)) {
			throw resp.error;
		}

		quoteAwning = resp.data.quote_accessory;
	} catch (err: any) {
		captureSentryError(err, {
			tags: {
				section: "redux"
			},
			extra: {
				redux_action: "updateQuoteAwning",
				file_origin: "src/Store/entities/Awnings/index.ts"
			}
		});

		const errorMessage = extractErrorMessage(err);
		return rejectWithValue(errorMessage);
	}
	return quoteAwning;
});

const setAwningsUnlocked = createAction<{
	quoteId: number;
	lineNumbers: number[];
	unlocked: boolean;
}>('entities/awnings/setUnlocked');

const setAwningsSelected = createAction<{
	quoteId: number;
	lineNumbers: number[];
	selected: boolean;
}>('entities/awnings/setSelected');

const AwningBuilder: extraEntityReducersFunction = (builder) => {


	builder.addCase(addQuoteAwning.pending, (state, action) => {
		const qaList = state.quoteAwnings[action.meta.arg.quoteId];


		const newQa: QuoteAccessoryStore = {
			...initialQuoteAwning(),
			parameters: action.meta.arg.parms,
			part_number: action.meta.arg.sku,
			quantity: 1,
			msrp: 0,
			cost: 0,
			shipping: 0,
			dealer_sale_price: 0,
			base_sale_price: 0,
			room_name: action.meta.arg.room_name ?? '',
			shade_name: action.meta.arg.shade_name ?? '',
			// Pricing is queued on the backend, so the row starts life with no price at all.
			is_pricing: true,
			quote_id: action.meta.arg.quoteId,
			line_number: action.meta.arg.nextLineNumber
		};

		// Sometimes the FE and the BE disagree on the next line item number, so key the placeholder
		// on the line number we asked for. `fulfilled` reconciles it against whatever the backend
		// actually assigned.
		if (qaList) {
			qaList.Items[action.meta.arg.nextLineNumber] = newQa;
		}
	});

	builder.addCase(addQuoteAwning.fulfilled, (state, action) => {
		const qaList = state.quoteAwnings[action.meta.arg.quoteId];

		if (!qaList) {
			throw new Error("Expected quote not found");
		}

		const expected = qaList.Items[action.payload.line_number];
		let expectedLineNumber = action.payload.line_number;

		// If the line item number we've expected doesn't link to the item we've just added, we need to find the correct line number.
		// Every awning shares one part number, so only match rows that are still unpriced placeholders --
		// matching on part number alone would clobber an already-priced awning.
		if (!expected) {
			const currentAwningsList = Object.entries(qaList.Items);
			const placeholder = currentAwningsList.find(([key, value]) => (
				value.part_number === action.payload.part_number
				&& value.is_pricing
				&& parseInt(key) === action.meta.arg.nextLineNumber
			)) ?? currentAwningsList.find(([_key, value]) => (
				value.part_number === action.payload.part_number
				&& value.is_pricing
				&& value.Id === 0
			));

			// The placeholder may already have been reconciled away (e.g. a refetch landed first).
			// Nothing to clean up in that case -- just write the row the backend gave us.
			expectedLineNumber = placeholder ? parseInt(placeholder[0]) : action.payload.line_number;
		}

		const pricedAcc = action.payload;

		if (pricedAcc) {
			const pricingFailed = pricedAcc.msrp === 0 && Boolean(pricedAcc.error_message?.trim());
			const newQa: QuoteAccessoryStore = {
				...pricedAcc,
				Id: pricedAcc.Id,
				quote_id: pricedAcc.quote_id,
				accessory_id: pricedAcc.accessory_id,
				quantity: pricedAcc.quantity,
				cost: pricedAcc.cost,
				msrp: pricedAcc.msrp,
				shipping: pricedAcc.shipping,
				last_time_priced: pricedAcc.last_time_priced,
				part_number: pricedAcc.part_number,
				line_number: pricedAcc.line_number,
				parameters: pricedAcc.parameters,
				error_message: pricedAcc.error_message ?? '',
				// The add response is always zeroed -- pricing is queued, not computed in the request --
				// so this stays `true` and the row keeps its spinner until the `AssemblyUpdated`
				// refetch brings the real numbers in.
				is_pricing: pricedAcc.msrp === 0 && !pricingFailed,
				unlocked: true,
				selected: false,
			};

			if (expectedLineNumber !== pricedAcc.line_number) {
				delete qaList.Items[expectedLineNumber];
			}

			qaList.Items[pricedAcc.line_number] = newQa;
		}
	});

	builder.addCase(addQuoteAwning.rejected, (state, action) => {
		const qaList = state.quoteAwnings[action.meta.arg.quoteId];
		if (!qaList) return;

		const { nextLineNumber, sku } = action.meta.arg;
		const items = { ...qaList.Items };

		Object.entries(items).forEach(([key, item]) => {
			if (item.part_number === sku && item.msrp === 0 && item.line_number === nextLineNumber) {
				delete items[Number(key)];
			}
		});

		qaList.Items = items;
	});

	builder.addCase(updateQuoteAwning.pending, (state, action) => {
		const qaList = state.quoteAwnings[action.meta.arg.quoteId];

		if (qaList) {
			const item = qaList.Items[action.meta.arg.lineNumber];

			if (item) {
				item.parameters = action.meta.arg.parms;
				if (action.meta.arg.room_name !== undefined) {
					item.room_name = action.meta.arg.room_name;
				}
				if (action.meta.arg.shade_name !== undefined) {
					item.shade_name = action.meta.arg.shade_name;
				}
				item.msrp = 0;
				item.cost = 0;
				item.shipping = 0;
				item.dealer_sale_price = 0;
				item.base_sale_price = 0;
				item.error_message = '';
				item.is_pricing = true;
			}
		}
	});

	builder.addCase(updateQuoteAwning.fulfilled, (state, action) => {
		const qaList = state.quoteAwnings[action.meta.arg.quoteId];

		if (qaList) {
			const pricedAcc = action.payload;

			if (pricedAcc) {
				const pricingFailed = pricedAcc.msrp === 0 && Boolean(pricedAcc.error_message?.trim());
				const newQa: QuoteAccessoryStore = {
					...pricedAcc,
					Id: pricedAcc.Id,
					quote_id: pricedAcc.quote_id,
					accessory_id: pricedAcc.accessory_id,
					quantity: pricedAcc.quantity,
					cost: pricedAcc.cost,
					msrp: pricedAcc.msrp,
					shipping: pricedAcc.shipping,
					last_time_priced: pricedAcc.last_time_priced,
					line_number: pricedAcc.line_number,
					parameters: pricedAcc.parameters,
					part_number: pricedAcc.part_number,
					error_message: pricedAcc.error_message ?? '',
					// The update response explicitly zeroes pricing -- a configuration change
					// invalidates the old price -- so this stays `true` until the `AssemblyUpdated`
					// refetch brings the requeued price in.
					is_pricing: pricedAcc.msrp === 0 && !pricingFailed,
					unlocked: qaList.Items[pricedAcc.line_number]?.unlocked ?? true,
					selected: qaList.Items[pricedAcc.line_number]?.selected ?? false,
				};

				qaList.Items = { ...qaList.Items, [pricedAcc.line_number]: newQa };
			}
		}
	});

	builder.addCase(loadQuoteAwnings.fulfilled, (state, action) => {
		try {
			let qaRecord = state.quoteAwnings[action.meta.arg];

			if (!qaRecord) {
				qaRecord = {
					loadStatus: loadStatus.fullyLoaded,
					Items: {}
				};
			}

			qaRecord.Items = action.payload.reduce((acc, cur) => {
				const existing = qaRecord.Items[cur.line_number];
				// `is_pricing` is a front end only flag, so the server never sends it. A load can only
				// *keep* a spinner alive, never start one: a row we already flagged as pricing that
				// still comes back unpriced is waiting on the queue, but a row we knew nothing about
				// is settled (a zeroed row there means pricing failed, not that it is in flight).
				const stillUnpriced = cur.msrp === 0 && !cur.error_message?.trim();

				acc[cur.line_number] = {
					...cur,
					error_message: cur.error_message ?? '',
					is_pricing: stillUnpriced && (existing?.is_pricing ?? false),
					unlocked: existing?.unlocked ?? false,
					selected: existing?.selected ?? false,
				};
				return acc;
			}, {} as Record<number, QuoteAccessoryStore>);

			state.quoteAwnings[action.meta.arg] = qaRecord;
		} catch (err) {
			const emptyQa = {
				loadStatus: loadStatus.fullyLoaded,
				Items: {}
			};

			state.quoteAwnings[action.meta.arg] = emptyQa;

			console.error(err);
			throw new Error("Error loading quote Awnings");
		}
	});

	builder.addCase(loadQuoteAwnings.pending, (state, action) => {
		let qaRecord = state.quoteAwnings[action.meta.arg];

		if (!qaRecord) {
			qaRecord = {
				loadStatus: loadStatus.loading,
				Items: {}
			};
		}

		state.quoteAwnings[action.meta.arg] = qaRecord;
	});
	builder.addCase(deleteQuoteAwning.pending, (state, action) => {
		// Remove the Awning from the quote.
		const qaList = state.quoteAwnings[action.meta.arg.quoteId];

		if (qaList) {
			const { [action.meta.arg.lineNumber]: omit, ...res } = qaList.Items;
			qaList.Items = res;
		}
	});

	builder.addCase(deleteQuoteAwning.fulfilled, (state, action) => {
		const qaList = state.quoteAwnings[action.meta.arg.quoteId];

		if (qaList) {
			const { [action.meta.arg.lineNumber]: omit, ...res } = qaList.Items;
			qaList.Items = res;
		}
	});

	builder.addCase(setAwningsUnlocked, (state, action) => {
		const qaList = state.quoteAwnings[action.payload.quoteId];
		if (!qaList) return;

		action.payload.lineNumbers.forEach((lineNumber) => {
			const item = qaList.Items[lineNumber];
			if (item) {
				qaList.Items[lineNumber] = { ...item, unlocked: action.payload.unlocked };
			}
		});
	});

	builder.addCase(setAwningsSelected, (state, action) => {
		const qaList = state.quoteAwnings[action.payload.quoteId];
		if (!qaList) return;

		action.payload.lineNumbers.forEach((lineNumber) => {
			const item = qaList.Items[lineNumber];
			if (item) {
				qaList.Items[lineNumber] = { ...item, selected: action.payload.selected };
			}
		});
	});
};

export {
	AwningBuilder,
	loadQuoteAwnings,
	addQuoteAwning,
	deleteQuoteAwning,
	updateQuoteAwning,
	setAwningsUnlocked,
	setAwningsSelected,
};
